import { ChangeEvent } from 'react';
import styled from 'styled-components';
import {
    FieldArray, Form, Formik, useFormik,
} from 'formik';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { units } from '../../helpers/styles/units';
import { typography } from '../../helpers/styles/typography';
import { FontWeight } from '../../constants/Styles/fontWeight';
import { TypesIcon } from '../../types/TypesIcon';
import { isEqualObject } from '../../helpers/isEqual';
import { addNotification } from '../../actions/notifications/addNotification';
import { TypesSnackbars } from '../../types/TypesSnackbars';
import { Input } from '../Input';
import { SubmitButtons } from '../StyledComponents';
import { UploadFiles } from '../UploadFiles';
import { AdditionalSettings, DocumentItem, UpdateDocumnets } from '../../types/Endpoints/documents/DocumentsContracts';
import { updateAdditionalSettings, updateDocuments } from '../../actions/docs';
import { colors } from '../../constants/Colors';
import { CopyToClipboard } from '../CopyToClipboard';

interface pageProps {
  pagesData: DocumentItem[];
  additionalSettings: AdditionalSettings;
}

type FormFields = {
    blocks: DocumentItem[];
    additionalSettings: AdditionalSettings;
}

export const StyledAppRatingWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${units(12)};
    margin-bottom: ${units(30)};
`;

const StyledInput = styled(Input)`
  font-weight: ${FontWeight.REGULAR};
  letter-spacing: ${units(0.4)};
  background-color: ${colors.light};
  ${typography(10)};
`;

const StyledMainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 75%;
  gap: ${units(30)};
  max-width: ${units(334)};
  padding-bottom: ${units(48)};
`;

const StyledForm = styled(Form)`
  width: 100%;
`;

const StyledSubmitButtons = styled.div`
  margin-left: -${units(12)};
`;

const StyledFlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(8)};
`;

const StyledSubTitle = styled.h3`
  font-weight: ${FontWeight.MEDIUM};
  letter-spacing: 0.8px;

  ${typography(10)};
`;

const StyledUrl = styled.h3`
  font-weight: ${FontWeight.REGULAR};
  letter-spacing: 0.8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-decoration: none;
  color: ${colors.grey20};

  ${typography(5)};
`;

const CopyToClipboardWrapper = styled.div`
  position: relative;
  display: inline-block;
  height: ${units(12)};
  width: ${units(12)};
`;

const StyledRowFlex = styled.div`
  display: flex;
  gap: ${units(8)};
  align-items: center;
`;

export const DocsForm = ({ pagesData, additionalSettings }: pageProps) => {
    const dispatch = useDispatch();

    const handleSubmit = (props: any) => {
        let body: UpdateDocumnets = {
            data: props.values.blocks.map((block: DocumentItem) => ({
                title: block.title,
                id: block.id,
                fileId: block.file?.id || null,
            })),
        };

        dispatch(updateDocuments(body));
        dispatch(updateAdditionalSettings({
            ratingValue: +props.values.additionalSettings.ratingValue,
            ratingCount: +props.values.additionalSettings.ratingCount,
        }));
    };

    const validationSchema = yup.object().shape({
        blocks: yup.array().of(
            yup.object().shape({
                title: yup.string(),
                url: yup.string(),
                file: yup.object().shape({
                    id: yup.number(),
                    path: yup.string(),
                    size: yup.number(),
                }),
            }),
        ),
        additionalSettings: yup.object().shape({
            ratingValue: yup.number().moreThan(0, 'Значение не может быть меньше 0').lessThan(5, 'Значение не может быть больше 5'),
            ratingCount: yup.number().moreThan(0, 'Значение не может быть меньше 0'),
        }),
    });

    const initialValues = {
        blocks: pagesData,
        additionalSettings: {
            ratingValue: additionalSettings?.ratingValue || 0,
            ratingCount: additionalSettings?.ratingCount || 0,
        },
    };

    const form = useFormik<FormFields>({
        initialValues,
        onSubmit: handleSubmit,
        validationSchema,
    });

    const handleResetForm = (clb: () => void, values: FormFields) => {
        clb();
        const equal = isEqualObject(form.initialValues, values);

        if (!equal) {
            dispatch(
                addNotification({
                    type: TypesSnackbars.ERROR,
                    message: {
                        title: 'Изменения отменены',
                        description: '',
                    },
                    icon: TypesIcon.SNACK_ERROR,
                }),
            );
        }
    };

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { value, name } = e.target;

        if (+value < 0 || (name === 'ratingValue' && +value > 5)) return;

        form.setFormikState(prev => (
            {
                ...prev,
                values: {
                    ...prev.values,
                    additionalSettings: {
                        ...prev.values.additionalSettings,
                        [name]: value,
                    },
                },
            }
        ));
    };

    return (
        <Formik<FormFields>
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            enableReinitialize
        >
            {props => (
                <StyledForm onSubmit={handleSubmit}>
                    <StyledAppRatingWrapper>
                        <StyledInput
                            name="ratingValue"
                            onChange={onChange}
                            value={form.values.additionalSettings.ratingValue}
                            placeholder="Оценка магазина"
                            typeInput="number"
                            title="Оценка магазина"
                            maxLength={255}
                            isTouched={form.touched?.additionalSettings?.ratingValue}
                            error={form.errors?.additionalSettings?.ratingValue}
                            count={form.submitCount}
                        />
                        <StyledInput
                            name="ratingCount"
                            onChange={onChange}
                            value={form.values.additionalSettings.ratingCount}
                            placeholder="Количество оценок"
                            typeInput="number"
                            title="Количество оценок"
                            isTouched={form.touched?.additionalSettings?.ratingCount}
                            error={form.errors?.additionalSettings?.ratingCount}
                            count={form.submitCount}
                        />
                    </StyledAppRatingWrapper>
                    <FieldArray name="blocks">
                        {() => (
                            <StyledMainWrapper>
                                {form.values.blocks?.map((block: DocumentItem, index: number) => (
                                    <StyledFlexWrapper key={`block-${block.id}`}>
                                        <StyledSubTitle>
                                            {form.values?.blocks[index].title || ''}
                                        </StyledSubTitle>
                                        <StyledRowFlex>
                                            <CopyToClipboardWrapper>
                                                <CopyToClipboard copyText={form.values?.blocks[index].url} />
                                            </CopyToClipboardWrapper>
                                            <StyledUrl>
                                                {form.values?.blocks[index].url || ''}
                                            </StyledUrl>
                                        </StyledRowFlex>

                                        <UploadFiles
                                            name={`blocks[${index}].file`}
                                            onChange={form.setFieldValue}
                                            value={form.values.blocks[index].file}
                                        />
                                    </StyledFlexWrapper>
                                ))}
                            </StyledMainWrapper>
                        )}
                    </FieldArray>
                    <StyledSubmitButtons>
                        <SubmitButtons
                            reset={() => handleResetForm(form.resetForm, form.values)}
                            submit={() => handleSubmit(form)}
                        />
                    </StyledSubmitButtons>
                </StyledForm>
            )}
        </Formik>
    );
};
